<template>
  <form-layout title="Edit Requested Stops" bottom-bar-stick-bottom @on-dismiss="tryDismiss">
    <vs-form ref="requestedStopForm">
      <JobInventoryMultiGroupsPicker
        :model-value="inventoryGroups"
        :resource-type-activity-id="jobActivityId ?? -1"
        is-location-required
        @update:modelValue="inventoryGroups = $event"
      />
    </vs-form>

    <template #snack-bar-wrapper>
      <vs-snackbar
        :value="showSnackbarError"
        sticky
        type="error"
        @input="showSnackbarError = false"
      >
        Failed to update stops and inventory
      </vs-snackbar>
    </template>

    <template #action-bar>
      <vs-button
        data-test="requested-stop-form-save-button"
        label="Save"
        :loading="saving"
        :disabled="!hasChanges"
        full-width
        @click="onSave"
      />
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsButton from '@/components/vision/VsButton.vue'
import { InventoryPickerGroup } from './InventoryPickers'
import JobInventoryMultiGroupsPicker from './InventoryPickers/JobInventoryMultiGroupsPicker.vue'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { v4 } from 'uuid'
import { JobStopType } from '@ironsight/types'
import { updateRequestedStops, useGetJobRequestedStopsQuery, useGetJobStopsQuery } from '@/api/jobs'
import { useQueryClient } from '@tanstack/vue-query'
import { JOBS_FILTERED_QUERY_KEY } from '@/components/jobs/job-search-page/useFilterJobsQuery'
import { isEqual, orderBy } from 'lodash'
import { standardizeInventoryList } from '@/utils/inventories'

// Props
const props = defineProps<{
  jobId: string
  jobNumber: number
  jobActivityId: number | undefined
}>()

// Composables
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

// Refs
const jobIdRef = toRef(props, 'jobId')
const requestedStopForm = ref<typeof VsForm>()
const inventoryGroups = ref<InventoryPickerGroup[]>([])
const saving = ref(false)
const showSnackbarError = ref(false)

const emit = defineEmits(['stops-updated'])

const queryClient = useQueryClient()
const { data: requestedStops } = useGetJobRequestedStopsQuery(jobIdRef)
const { data: actualStops } = useGetJobStopsQuery(jobIdRef)

const sortedRequestedStops = computed(() =>
  orderBy(requestedStops.value, ['position', 'stopType'], ['asc', 'desc']),
)
const hasChanges = computed(() => {
  const standardizedRequestedStops = sortedRequestedStops.value.map((rs) => ({
    locationId: rs.locationId,
    inventory: standardizeInventoryList(
      rs.inventory.map((ri) => ({
        quantity: ri.quantity,
        inventorySubTypeId: ri.inventorySubTypeId,
      })),
    ),
  }))

  const standardizedInventoryGroups = inventoryGroups.value?.map((i) => ({
    locationId: i.locationId,
    inventory: standardizeInventoryList(
      i.inventory.map((ri) => ({
        quantity: ri.quantity,
        inventorySubTypeId: ri.inventorySubTypeId,
      })),
    ),
  }))

  return !isEqual(standardizedRequestedStops, standardizedInventoryGroups)
})

// Methods
function isValid() {
  // Depending on your IDE settings you may see warning here for calling `isValid`.
  // It is a false positive as the VsForm is not composition API yet
  return requestedStopForm.value?.isValid()
}

function dismiss() {
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
}

function tryDismiss() {
  let shouldDismiss = true
  if (hasChanges.value) {
    shouldDismiss = window.confirm(
      'The stop and inventory changes have not been submitted, are you sure you want to exit?',
    )
  }

  if (shouldDismiss) {
    dismiss()
  }
}

async function onSave() {
  if (!isValid()) return

  saving.value = true

  try {
    const inventoryPayload = inventoryGroups.value.map((x, i) => ({
      id: x.id,
      locationId: x.locationId ?? null,
      position: i,
      stopType: x.stopType,
      inventory: x.inventory
        .filter((i) => i.inventorySubTypeId !== null && i.quantity !== null)
        .map((i) => ({
          id: i.id,
          quantity: i.quantity!,
          inventorySubTypeId: i.inventorySubTypeId!,
        })),
    }))

    await updateRequestedStops(props.jobId, inventoryPayload)

    await Promise.all([
      queryClient.invalidateQueries(['requested-stops', 'byJobId', props.jobId]),
      queryClient.invalidateQueries([JOBS_FILTERED_QUERY_KEY]),
      queryClient.invalidateQueries(['jobs', 'search']),
      queryClient.invalidateQueries(['history', 'byJobNumber', props.jobNumber]),
    ])

    emit('stops-updated')
    dismiss()
  } catch (e) {
    showSnackbarError.value = true
  } finally {
    saving.value = false
  }
}

// Watchers
watch(
  [sortedRequestedStops, actualStops],
  ([newSortedRequestedStops, newActualStops]) => {
    if (newSortedRequestedStops && newActualStops) {
      inventoryGroups.value = newSortedRequestedStops.map((x) => ({
        ...x,
        _uniqueId: v4(),
        inventory: x.inventory.map((y) => ({
          ...y,
          _uniqueId: v4(),
        })),
        isDisabled:
          (x.stopType === JobStopType.PickUp &&
            newActualStops.some((s) => s.requestedStopId === x.id)) ||
          (x.stopType === JobStopType.DropOff &&
            newActualStops.some((s) => s.stopType === JobStopType.DropOff)),
      }))
    }
  },
  { immediate: true },
)
</script>
